import type { EstimatedRaceStage, RunoffTerms, WinRule } from '../generated/dtos.gen';
import { CycleKind } from '../generated/enums.gen';
import { assertNever } from '../utils/assertNever';

/** A Win Rule whose places are known. Only a Refused stage's vote-for-several rule can leave its seats unknown. */
export type SeatedRule = Exclude<WinRule, { kind: 'VoteForSeveral' }> | { kind: 'VoteForSeveral'; seats: number };

/**
 * What the math reads from a stage: {@link estimatedContest} for a catalog stage, or {@link manualContest}, whose
 * number is the candidate's own, so it has no range we could call it outside of.
 */
export type Contest = Pick<EstimatedRaceStage, 'cycle' | 'numbers' | 'lastResult'> & { rule: SeatedRule; manual?: true };

/** Throws a RangeError on unknown seats, which the reader refuses on an Estimated stage. */
export function estimatedContest(stage: EstimatedRaceStage): Contest {
  const { rule, cycle, numbers, lastResult } = stage;
  if (rule.kind === 'VoteForSeveral' && rule.seats === null) throw new RangeError('an Estimated stage must give its seats');
  return { rule: rule as SeatedRule, cycle, numbers, lastResult };
}

export interface Dials {
  /** Ballots cast on the year dial, before draw and nudge. Starts at the busy turnout. */
  year: number;
  /** Top-of-ballot draw, in points of turnout among registered voters. */
  draw: number;
  /** 0 nobody knows it's happening, 0.5 like races like it, 1 mailers every day. */
  heat: number;
  candidates: number;
  /** Points above 50% in a two-way race: 3 aims at 53%. */
  cushion: number;
  /** Percent added to ballots. */
  nudge: number;
}

export interface VoteGoal {
  /** Ballots expected in the stage's electorate. */
  ballots: number;
  /** Ballots that mark this Race. */
  votes: number;
  /** Share of votes that wins or advances, before the cushion; 0 when nobody can lose. */
  share: number;
  /** The Vote Goal. 1 when the field is no bigger than the places. */
  aim: number;
  /** The aim at {@link defaultDials}: our own number. */
  atRest: number;
  /** What wins a usual year: the same dials at the usual turnout, with no cushion. */
  usualAim: number;
  /** The factors behind {@link ballots} and {@link aim}. */
  working: GoalWorking;
  /** Set when ballots are more than 10% past the busiest or quietest turnout we expect. */
  outside: 'High' | 'Low' | null;
  /** Set when the rule has runoff terms and three or more are running. */
  runoff: RunoffGoal | null;
}

/** The factors the ballots and the aim are worked out with, so a screen can show the math without redoing it. */
export interface GoalWorking {
  /** Ballots on the year dial × this, for what's above the Race on the ballot. 1 with no draw. */
  draw: number;
  /** × this, for the candidate's turnout change. 1 with no nudge. */
  nudge: number;
  /** Votes × share × this is the aim. Null when the aim is one vote past the bare win instead. */
  cushion: number | null;
}

export interface RunoffGoal {
  /** First-round votes that guarantee a top-two finish. */
  toMakeIt: number;
  /** Runoff ballots, at the usual share of first-round votes. */
  votes: number;
  /** The aim in the two-way runoff, at the same cushion. */
  aim: number;
}

export const DIAL_LIMITS = {
  heat: { min: 0, max: 1 },
  cushion: { min: 0, max: 5 },
  nudge: { min: -10, max: 10 },
} as const;

/** A mark on the year dial: one of this stage's past elections, or a point of its quiet-to-busy window. */
export type YearAnchor =
  | { kind: 'Past'; year: number; ballots: number }
  | { kind: 'Window'; point: 'Quiet' | 'Usual' | 'Busy'; ballots: number };

export interface YearDial {
  /** Ascending by ballots. Empty for a manual contest, whose dial is fixed at the typed number. */
  anchors: YearAnchor[];
  min: number;
  max: number;
}

/** Where a year-dial value sits among the anchors, for the web to word ("a 2018-type year"). */
export type YearPosition =
  | { kind: 'Near'; anchor: YearAnchor }
  | { kind: 'Between'; lower: YearAnchor; upper: YearAnchor }
  | { kind: 'Below'; anchor: YearAnchor }
  | { kind: 'Above'; anchor: YearAnchor };

export type DrawTickId = 'None' | 'Usual' | 'Race' | 'Measure' | 'Huge';

export interface DrawTick {
  id: DrawTickId;
  points: number;
}

export interface ManualNumbers {
  /** Votes cast in this Race last time, as the results page shows them. */
  votesLastTime: number;
  registered: number | null;
  /** Seats up next time; given exactly when the rule leaves them unknown. */
  seats?: number;
}

/**
 * Where every dial starts: a busy year, so the goal wins even if turnout runs high (a candidate who plans for a usual
 * year can lose to a high one); no draw, middle heat, 3 points of cushion, and last time's field or one more running
 * than places, whichever is bigger.
 */
export function defaultDials(contest: Contest): Dials {
  const field = Math.max(placesOf(contest.rule) + 1, contest.lastResult?.candidates ?? 0);
  return { year: contest.numbers.turnout.busy, draw: 0, heat: 0.5, candidates: Math.min(field, maxCandidates(contest)), cushion: 3, nudge: 0 };
}

/** The most candidates the field dial offers: 12, or twice the places for a big board. */
export function maxCandidates(contest: Contest): number {
  return Math.max(12, 2 * placesOf(contest.rule));
}

/** Clamps every dial and rounds it to stored precision. */
export function settleDials(contest: Contest, dials: Dials): Dials {
  const year = yearDial(contest);
  const draw = drawRange(contest.cycle);
  return {
    year: clamp(Math.round(dials.year), Math.round(year.min), Math.round(year.max)),
    draw: round(clamp(dials.draw, draw.min, draw.max), 2),
    heat: round(clamp(dials.heat, DIAL_LIMITS.heat.min, DIAL_LIMITS.heat.max), 2),
    candidates: clamp(Math.round(dials.candidates), 1, maxCandidates(contest)),
    cushion: round(clamp(dials.cushion, DIAL_LIMITS.cushion.min, DIAL_LIMITS.cushion.max), 1),
    nudge: round(clamp(dials.nudge, DIAL_LIMITS.nudge.min, DIAL_LIMITS.nudge.max), 1),
  };
}

export function voteGoal(contest: Contest, dials: Dials): VoteGoal {
  const usualYear = { ...dials, year: contest.numbers.turnout.usual, cushion: 0 };
  return { ...goalAt(contest, dials), atRest: goalAt(contest, defaultDials(contest)).aim, usualAim: goalAt(contest, usualYear).aim };
}

/** The year dial: past years when there are two or more, the quiet-to-busy window otherwise, with room past both ends. */
export function yearDial(contest: Contest): YearDial {
  const { turnout, pastTurnout } = contest.numbers;
  if (contest.manual) return { anchors: [], min: turnout.usual, max: turnout.usual };
  const anchors: YearAnchor[] =
    pastTurnout.length >= 2
      ? pastTurnout.map((p) => ({ kind: 'Past', year: p.year, ballots: p.ballots }))
      : [
          { kind: 'Window', point: 'Quiet', ballots: turnout.quiet },
          { kind: 'Window', point: 'Usual', ballots: turnout.usual },
          { kind: 'Window', point: 'Busy', ballots: turnout.busy },
        ];
  anchors.sort((a, b) => a.ballots - b.ballots);
  const ballots = anchors.map((a) => a.ballots);
  return { anchors, min: Math.min(turnout.quiet, ...ballots) * 0.94, max: Math.max(turnout.busy, ...ballots) * 1.06 };
}

/** Near when within 4% of an anchor; otherwise between two, or past an end. Null when the dial has no anchors. */
export function yearPosition(dial: YearDial, ballots: number): YearPosition | null {
  const { anchors } = dial;
  if (anchors.length === 0) return null;
  const near = anchors.find((a) => Math.abs(ballots / a.ballots - 1) <= 0.04);
  if (near) return { kind: 'Near', anchor: near };
  if (ballots < anchors[0].ballots) return { kind: 'Below', anchor: anchors[0] };
  const upper = anchors.findIndex((a) => a.ballots > ballots);
  if (upper === -1) return { kind: 'Above', anchor: anchors[anchors.length - 1] };
  return { kind: 'Between', lower: anchors[upper - 1], upper: anchors[upper] };
}

/**
 * The draw dial's marks, against base years that had their own top of the ticket. A statewide ballot measure adds 1.7
 * points in a midterm and 0.7 in a presidential year (Tolbert and Smith 2005); the close-race and huge marks are our
 * guesses.
 */
export function drawTicks(cycle: CycleKind): DrawTick[] {
  const presidential = cycle === CycleKind.Presidential;
  return [
    { id: 'None', points: presidential ? -0.7 : -1.7 },
    { id: 'Usual', points: 0 },
    { id: 'Race', points: presidential ? 0.5 : 1.0 },
    { id: 'Measure', points: presidential ? 0.7 : 1.7 },
    { id: 'Huge', points: presidential ? 2 : 4 },
  ];
}

export function drawRange(cycle: CycleKind): { min: number; max: number } {
  return cycle === CycleKind.Presidential ? { min: -1.5, max: 3 } : { min: -2.5, max: 5 };
}

/**
 * A contest from numbers the candidate typed: for a Refused stage (keep its rule and cycle), a Race type we lack, or a
 * state we don't cover. The typed number already counts only this Race's votes, so every ballot marks the Race.
 * Throws a {@link NumberRefused} on numbers the form should have refused.
 */
export function manualContest(rule: WinRule, cycle: CycleKind, typed: ManualNumbers): Contest {
  const refused = manualRefusal(rule, typed);
  if (refused) throw new NumberRefused(refused);
  const { votesLastTime, registered, seats } = typed;
  return {
    rule: seated(rule, seats),
    cycle,
    manual: true,
    lastResult: null,
    numbers: {
      turnout: { quiet: votesLastTime, usual: votesLastTime, busy: votesLastTime },
      pastTurnout: [],
      registered,
      shareVotingInRace: 1,
    },
  };
}

/** A number the candidate typed that the math refuses, named so a screen can mark its box, and why in the math's terms. */
export interface Refusal<Field extends string> {
  field: Field;
  message: string;
}

/** The first typed number {@link manualContest} would refuse, or null when it takes them all. */
export function manualRefusal(rule: WinRule, typed: ManualNumbers): Refusal<keyof ManualNumbers> | null {
  const { votesLastTime, registered, seats } = typed;
  if (!Number.isInteger(votesLastTime) || votesLastTime <= 0)
    return { field: 'votesLastTime', message: `votesLastTime must be a whole number above 0, not ${votesLastTime}` };
  if (registered !== null && (!Number.isInteger(registered) || registered < votesLastTime))
    return { field: 'registered', message: `registered must be a whole number of at least ${votesLastTime} votes, not ${registered}` };
  const seatsUnknown = rule.kind === 'VoteForSeveral' && rule.seats === null;
  if (!seatsUnknown && seats !== undefined) return { field: 'seats', message: `seats are set by the rule, so they can't be typed as ${seats}` };
  if (seatsUnknown && (seats === undefined || !Number.isInteger(seats) || seats < 2))
    return { field: 'seats', message: `seats must be a whole number of at least 2 when the rule leaves them unknown, not ${seats}` };
  return null;
}

/** Takes seats checked by {@link manualRefusal}. */
function seated(rule: WinRule, seats: number | undefined): SeatedRule {
  return rule.kind === 'VoteForSeveral' && rule.seats === null ? { kind: 'VoteForSeveral', seats: seats as number } : (rule as SeatedRule);
}

/** Thrown when numbers a {@link Refusal} would have stopped reach the math anyway. */
export class NumberRefused extends RangeError {
  readonly field: keyof ManualNumbers | keyof OwnNumbers;

  constructor(refusal: Refusal<keyof ManualNumbers | keyof OwnNumbers>) {
    super(refusal.message);
    this.name = 'NumberRefused';
    this.field = refusal.field;
  }
}

/** Numbers the candidate knows better than ours. Each one left out stays ours. */
export interface OwnNumbers {
  /** Ballots they expect in this stage's electorate. */
  usual?: number;
  /** Share of ballots that mark this Race, in (0, 1]. */
  shareVotingInRace?: number;
  registered?: number | null;
}

/**
 * The contest with the candidate's own numbers in place of ours. Their own turnout is one number, not a range, so the
 * year dial stays on it the way a manual contest's does. Throws a {@link NumberRefused} on numbers the form should have refused.
 */
export function withOwnNumbers(contest: Contest, own: OwnNumbers): Contest {
  const refused = ownRefusal(contest, own);
  if (refused) throw new NumberRefused(refused);
  const numbers = { ...contest.numbers };
  let manual = contest.manual;
  if (own.usual !== undefined) {
    numbers.turnout = { quiet: own.usual, usual: own.usual, busy: own.usual };
    manual = true;
  }
  if (own.shareVotingInRace !== undefined) numbers.shareVotingInRace = own.shareVotingInRace;
  if (own.registered !== undefined) numbers.registered = own.registered;
  return manual ? { ...contest, numbers, manual } : { ...contest, numbers };
}

/** The first own number {@link withOwnNumbers} would refuse, or null when it takes them all. */
export function ownRefusal(contest: Contest, own: OwnNumbers): Refusal<keyof OwnNumbers> | null {
  if (own.usual !== undefined && (!Number.isInteger(own.usual) || own.usual <= 0))
    return { field: 'usual', message: `usual must be a whole number above 0, not ${own.usual}` };
  if (own.shareVotingInRace !== undefined && !(own.shareVotingInRace > 0 && own.shareVotingInRace <= 1))
    return { field: 'shareVotingInRace', message: `shareVotingInRace must be above 0 and at most 1, not ${own.shareVotingInRace}` };
  const registered = own.registered !== undefined ? own.registered : contest.numbers.registered;
  const busy = own.usual ?? contest.numbers.turnout.busy;
  if (registered !== null && (!Number.isInteger(registered) || registered < busy))
    return { field: 'registered', message: `registered must be a whole number of at least ${busy}, not ${registered}` };
  return null;
}

/** Where a typed goal sits, and the goals of the quiet and busy years it was checked against. */
export interface OwnGoalCheck {
  sits: 'Low' | 'Within' | 'High';
  quietAim: number;
  busyAim: number;
}

/** Where a goal the candidate typed sits against the goals a quiet and a busy year need, at their other dials. */
export function checkOwnGoal(contest: Contest, dials: Dials, aim: number): OwnGoalCheck {
  const { quiet, busy } = contest.numbers.turnout;
  const quietAim = goalAt(contest, { ...dials, year: quiet }).aim;
  const busyAim = goalAt(contest, { ...dials, year: busy }).aim;
  return { sits: aim < quietAim ? 'Low' : aim > busyAim ? 'High' : 'Within', quietAim, busyAim };
}

/** For a state we don't cover, where the candidate typed only a date. */
export function cycleOfDate(isoDate: string): CycleKind {
  const year = Number(isoDate.slice(0, 4));
  if (year % 2 === 1) return CycleKind.OffCycle;
  return year % 4 === 0 ? CycleKind.Presidential : CycleKind.Midterm;
}

/** The places a Win Rule fills. Null only for a vote-for-several rule whose seats aren't known. */
export function placesOf(rule: SeatedRule): number;
export function placesOf(rule: WinRule): number | null;
export function placesOf(rule: WinRule): number | null {
  switch (rule.kind) {
    case 'MostVotes':
    case 'MajorityOrRunoff':
    case 'ShareThreshold':
      return 1;
    case 'VoteForSeveral':
      return rule.seats;
    case 'TopAdvance':
      return rule.count;
    default:
      return assertNever(rule);
  }
}

/** The runoff when nobody wins outright, for a rule that has one and knows its turnout. */
export function runoffTermsOf(rule: WinRule): RunoffTerms | null {
  switch (rule.kind) {
    case 'MostVotes':
    case 'VoteForSeveral':
    case 'TopAdvance':
      return null;
    case 'MajorityOrRunoff':
    case 'ShareThreshold':
      return rule.runoff;
    default:
      return assertNever(rule);
  }
}

/** With two running, a threshold rule is decided by the majority. */
function outrightShare(rule: SeatedRule, candidates: number): number {
  switch (rule.kind) {
    case 'MostVotes':
      return shareNeeded(candidates, 1);
    case 'VoteForSeveral':
      return shareNeeded(candidates, rule.seats);
    case 'TopAdvance':
      return shareNeeded(candidates, rule.count);
    case 'MajorityOrRunoff':
      return candidates > 1 ? 0.5 : 0;
    case 'ShareThreshold':
      return candidates > 1 ? Math.max(rule.share, shareNeeded(candidates, 1)) : 0;
    default:
      return assertNever(rule);
  }
}

function goalAt(contest: Contest, dials: Dials): Omit<VoteGoal, 'atRest' | 'usualAim'> {
  const { turnout, registered, shareVotingInRace } = contest.numbers;
  const rate = registered ? Math.min(0.95, turnout.usual / registered) : 0.5;
  const working: GoalWorking = { draw: 1 + dials.draw / (100 * rate), nudge: 1 + dials.nudge / 100, cushion: cushionFactor(dials.cushion) };
  const ballots = Math.round(dials.year * working.draw * working.nudge);
  const votes = Math.round(ballots * markingAt(shareVotingInRace, dials.heat));
  const share = outrightShare(contest.rule, dials.candidates);
  const terms = runoffTermsOf(contest.rule);
  return {
    ballots,
    votes,
    share,
    aim: aimFor(votes, share, working.cushion),
    outside: contest.manual ? null : ballots > turnout.busy * 1.1 ? 'High' : ballots < turnout.quiet * 0.9 ? 'Low' : null,
    runoff: terms && dials.candidates >= 3 ? runoffGoal(votes, terms, dials.candidates, working.cushion) : null,
    working,
  };
}

function runoffGoal(votes: number, terms: RunoffTerms, candidates: number, cushion: number | null): RunoffGoal {
  const runoffVotes = Math.round(votes * terms.turnout.usual);
  return {
    toMakeIt: aimFor(votes, shareNeeded(candidates, 2), cushion),
    votes: runoffVotes,
    aim: aimFor(runoffVotes, 0.5, cushion),
  };
}

/**
 * Emerge's vote-goal rules: 2 running, 50%; 3-5, 1/(n-1); 6-9, 1/(n-3); 10+, 1/(n-7). Taken literally they rise from
 * 5 candidates to 6 and from 9 to 10, so the share holds instead. With several places, more than 1/(places+1) of
 * ballots guarantees one, and extra candidates beyond the first loser cut it the same way.
 */
function shareNeeded(candidates: number, places: number): number {
  if (candidates <= places) return 0;
  let share = 0.5;
  for (let n = 2; n <= candidates - places + 1; n++) share = Math.min(share, emerge(n));
  return (share / 0.5) * (1 / (places + 1));
}

function emerge(n: number): number {
  if (n <= 2) return 0.5;
  if (n <= 5) return 1 / (n - 1);
  if (n <= 9) return 1 / (n - 3);
  return 1 / (n - 7);
}

/** The cushion is points above 50% in a two-way race (3 aims at 53%), scaled the same way for any share. */
function cushionFactor(cushion: number): number | null {
  return cushion <= 0 ? null : (50 + cushion) / 50;
}

/**
 * Products are snapped to a millionth before rounding: 6220 × 0.5 × 1.1 is 3421.0000000000005 in floating point, which
 * ceil would make 3422. A true fraction of a vote here is never that small.
 */
function aimFor(votes: number, share: number, cushion: number | null): number {
  if (share <= 0) return 1;
  if (cushion === null) return Math.floor(snapped(votes * share)) + 1;
  return Math.ceil(snapped(votes * share * cushion));
}

function snapped(value: number): number {
  return Math.round(value * 1e6) / 1e6;
}

/**
 * Heat sets the share of ballots that mark the Race. The middle is the measured share; cold skips at least a quarter
 * (the top of the 5-25% range Christopher Mann gives); hot keeps 98% (on-cycle local races in New York mostly lose
 * under 10%, often about 1%: Citizens Union 2025).
 */
function markingAt(shareVotingInRace: number, heat: number): number {
  const skip = 1 - shareVotingInRace;
  const cold = Math.max(0.25, skip + 0.1);
  const hot = Math.min(skip, 0.02);
  const h = clamp(heat, 0, 1);
  return 1 - (h <= 0.5 ? cold + ((skip - cold) * h) / 0.5 : skip + ((hot - skip) * (h - 0.5)) / 0.5);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function round(value: number, places: number): number {
  return Math.round(value * 10 ** places) / 10 ** places;
}
